import { dequal } from 'dequal';
import {
  InterviewQuestions,
  ProjectCreationValues,
  ProjectProps,
  ScreeningQuestion,
} from '@/types';
import { isInterviewQuestionsEmpty } from './questionGroupHelpers';

/**
 * The top-level ProjectCreationValues keys that participate in save-payload
 * diffing. `snippets` (the Design page's Context step) belongs here: the
 * TELV1-490 rewrite excluded it on the incorrect premise that the server
 * doesn't accept it, so a Context-only edit produced an empty payload — the
 * text was never persisted and the user got the generic error toast
 * (Release 3.5.0 regression, TELV1-512).
 */
export const PROJECT_DIFF_FIELDS: (keyof ProjectCreationValues)[] = [
  'type_of_interview',
  'interviewer_name',
  'company_name',
  'interview_goal',
  'interview_audience',
  'tone_of_voice',
  'snippets',
  'language',
  'allow_language_selection',
  'privacy_policy',
  'analysis_language',
  'theme_id',
  'opening_message',
  'respondents_max',
  'interview_questions',
  'screening_questions',
];

// Fields whose form representation is `'' | undefined` while the server can
// return `null | undefined` — all three are semantically "unset".
const EMPTY_STRING_EQUIVALENT_KEYS = new Set<keyof ProjectCreationValues>([
  'interviewer_name',
  'company_name',
  'interview_goal',
  'interview_audience',
  'tone_of_voice',
  'language',
  'privacy_policy',
  'theme_id',
  'opening_message',
]);

/**
 * Recursively normalizes every `media` array in a question structure
 * (interview-question tree OR flat screening list) down to its STABLE parts:
 * drops the signed `url` (re-generated on every fetch, so it differs even when
 * nothing changed) and any runtime `File` entries (non-serializable, uploaded
 * via the separate media endpoint — never part of this JSON payload), while
 * keeping identity, `label` and order so real media edits register as
 * changes. A media key that is empty / legacy `""` / a bare `File` is dropped
 * entirely so it compares equal to a question that never had media.
 *
 * Replaces the old full `stripMedia`: stripping media wholesale made a
 * label-only or reorder-only save produce an EMPTY payload — the user got
 * "Error, please try again" and the edit was silently lost (the TELV1-400
 * signature, media edition).
 *
 * Dual-use on purpose: this is both the comparison normalizer (see
 * `normalizeProjectFieldValue`) and the outgoing-payload sanitizer for
 * `interview_questions` (ProjectUpdateDTO doesn't validate the tree, so
 * without it every save writes stale signed URLs into the DB).
 */
export const stripVolatileMediaFields = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stripVolatileMediaFields);
  if (value && typeof value === 'object') {
    // A bare File as a non-media value passes through untouched: only media
    // arrays may legally contain Files, and those are filtered below. This
    // early return just avoids Object.entries() mangling a File instance.
    if (typeof File !== 'undefined' && value instanceof File) return value;
    const { media, ...rest } = value as Record<string, unknown>;
    const cleanedRest = Object.fromEntries(
      Object.entries(rest).map(([key, v]) => [key, stripVolatileMediaFields(v)]),
    );
    if (!('media' in (value as Record<string, unknown>))) return cleanedRest;
    if (!Array.isArray(media) || media.length === 0) return cleanedRest;
    const normalizedMedia = media
      .filter(
        (item) =>
          item &&
          typeof item === 'object' &&
          !(typeof File !== 'undefined' && item instanceof File),
      )
      .map((item) => {
        const { url: _url, ...mediaRest } = item as Record<string, unknown>;
        return mediaRest;
      });
    if (normalizedMedia.length === 0) return cleanedRest;
    return { ...cleanedRest, media: normalizedMedia };
  }
  return value;
};

// Screening questions may carry keys explicitly set to `undefined` (e.g. a
// NUMERIC question's `responses` key) depending on which code path built the
// object. `dequal` treats `{ a: undefined }` and `{}` as different (distinct
// key counts), so normalize both sides by dropping undefined-valued keys
// before comparing.
const stripUndefinedKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stripUndefinedKeys);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([key, v]) => [key, stripUndefinedKeys(v)]),
    );
  }
  return value;
};

/**
 * Normalizes ONE screening question for comparison (TELV1-614). Every rule
 * here hides a difference that carries no meaning, so a question the user
 * did not touch compares equal to its server copy:
 *
 * - `randomize` / `quotas_enabled` missing ≡ `false`. The server's DTO
 *   class initialises `randomize = false`, so a question created in the
 *   editor without the key came back from the save WITH it — the form then
 *   reported `screening_questions` as changed forever and "Save changes"
 *   never settled.
 * - `responses: null` ≡ no `responses` key (NUMERIC questions).
 * - `responses[].quota_notified` is server-owned: the worker sets it when a
 *   quota fills and `projects.service` strips it from every incoming save.
 *   Comparing it made any later save on a PUBLISHED project carry
 *   `screening_questions` and fail with CANNOT_CHANGE_FIELDS_ON_PUBLISHED.
 *
 * Compare-only: the outgoing payload stays the raw form value.
 */
export const normalizeScreeningQuestionForCompare = (
  question: ScreeningQuestion,
): Record<string, unknown> => {
  // `responses` can arrive as null and with the server-only `quota_notified`
  // key, neither of which the frontend type models.
  const { responses, ...rest } = question as Omit<ScreeningQuestion, 'responses'> & {
    responses?: Array<Record<string, unknown>> | null;
  };
  return {
    ...rest,
    randomize: question.randomize ?? false,
    quotas_enabled: question.quotas_enabled ?? false,
    ...(responses == null
      ? {}
      : {
          responses: responses.map(({ quota_notified: _quotaNotified, ...response }) => response),
        }),
  };
};

/**
 * Drops empty/whitespace-only entries from a snippets (Context step) list.
 * Used both by the `snippets` normalization case below and to sanitize the
 * outgoing save payload — the server should never store blank rows, which
 * are a UI affordance (the form's `['']` initialization, "add context"
 * placeholder rows), not content (TELV1-512).
 */
export const pruneEmptySnippets = (rawValue: unknown): string[] =>
  (Array.isArray(rawValue) ? rawValue : []).filter(
    (entry): entry is string => typeof entry === 'string' && entry.trim() !== '',
  );

/**
 * Normalizes a single ProjectCreationValues field so that semantically
 * equivalent-but-differently-shaped form/server values compare equal. See
 * the TELV1-490 architect review normalization table for the source of each
 * rule.
 */
export const normalizeProjectFieldValue = (
  key: keyof ProjectCreationValues,
  rawValue: unknown,
): unknown => {
  if (EMPTY_STRING_EQUIVALENT_KEYS.has(key)) {
    return rawValue === null || rawValue === undefined || rawValue === '' ? '' : rawValue;
  }

  switch (key) {
    case 'type_of_interview':
      return Array.isArray(rawValue) && rawValue.length === 0 ? undefined : rawValue;

    case 'allow_language_selection':
      // `false` ("No"), `true` ("Yes") and unset are three distinct UI states —
      // the radio renders `false` as "No" selected, unset as no selection. Only
      // unset collapses to `null`; `false` must stay `false` so that actively
      // picking "No" on a fresh project (whose server value is unset) is detected
      // as a change and persisted. Previously `false` was collapsed to `null`,
      // making "No" indistinguishable from unset, so the value-diff treated it as
      // unchanged and the save silently dropped it (TELV1-510).
      return rawValue === undefined ? null : rawValue;

    case 'analysis_language':
      // Unset (''/null/undefined) collapses to `null`. Do NOT default unset to
      // 'English': that made an explicit 'English' selection compare equal to an
      // unset server value, so the diff dropped it and 'English' could never be
      // saved on a project whose stored value was unset (TELV1-510). A non-unset
      // value (incl. 'English') now compares distinctly from unset.
      return rawValue === '' || rawValue == null ? null : rawValue;

    case 'respondents_max':
      return rawValue === 0 || rawValue === undefined ? null : rawValue;

    case 'interview_questions': {
      // Volatile-only media strip (see stripVolatileMediaFields): keeps
      // label/order/identity in the comparison so a label-only or
      // reorder-only stimulus edit produces a real save payload, while the
      // per-fetch signed `url` and runtime `File`s stay out of it. The old
      // full media strip is what made such saves come out EMPTY — error
      // toast, edit silently lost (TELV1-400 signature, media edition).
      // Undefined-valued keys are dropped too, exactly as for screening
      // questions below (media first, so runtime Files are removed before
      // they could be flattened): an editor that registers a field the
      // server copy doesn't carry (QuestionBox's `follow_up_question`) left
      // `{ follow_up_question: undefined }` vs `{}`, which `dequal` calls a
      // change. A screening-only edit then also flagged interview_questions,
      // and no save could clear it — the Save button stayed enabled with an
      // empty label (TELV1-607). JSON drops undefined keys anyway, so the
      // server never received a difference.
      const questions = rawValue as InterviewQuestions | undefined;
      return isInterviewQuestionsEmpty(questions)
        ? null
        : stripUndefinedKeys(stripVolatileMediaFields(questions));
    }

    case 'screening_questions': {
      const list = (rawValue as ScreeningQuestion[] | undefined) || [];
      // Same volatile-only media strip as interview_questions (TELV1-520).
      // Media normalization runs FIRST: stripUndefinedKeys would flatten a
      // runtime `File` (no enumerable own props) into `{}` before the File
      // filter could recognise it.
      return list.length === 0
        ? undefined
        : stripUndefinedKeys(
            stripVolatileMediaFields(list.map(normalizeScreeningQuestionForCompare)),
          );
    }

    case 'snippets': {
      // The form initializes snippets to [''] when the server has none
      // (ProjectCreate's reset effect), and users can add blank rows that
      // carry no content — so empty/whitespace-only entries are ignored and
      // [''], [] and unset all compare equal (TELV1-512).
      const pruned = pruneEmptySnippets(rawValue);
      return pruned.length === 0 ? undefined : pruned;
    }

    default:
      return rawValue;
  }
};

/**
 * True when `formValue` and `serverValue` are semantically the same for the
 * given field, after normalization. Used both for save-payload diffing and
 * for the Save-button enable signal, so the two never disagree (TELV1-490
 * architect review, Point 1a).
 */
export const isProjectFieldUnchanged = (
  key: keyof ProjectCreationValues,
  formValue: unknown,
  serverValue: unknown,
): boolean =>
  dequal(normalizeProjectFieldValue(key, formValue), normalizeProjectFieldValue(key, serverValue));

/**
 * Value-based diff between the current form values and the last-known
 * server state, across the fixed `PROJECT_DIFF_FIELDS` key set. Deliberately
 * ignores `dirtyFields` — React Hook Form's dirty tracking can miss real
 * changes (programmatic `setValue` without `shouldDirty`, drag-reorder,
 * TELV1-400) or flag changes that aren't semantically real ('' vs null,
 * false vs null, etc — see the normalization table above). Comparing actual
 * values against the actual server state is the only signal that can't lie
 * in either direction.
 */
export const getChangedProjectFields = (
  formValues: ProjectCreationValues,
  serverValues: ProjectProps | undefined,
): Partial<ProjectCreationValues> => {
  const changed: Record<string, unknown> = {};

  PROJECT_DIFF_FIELDS.forEach((key) => {
    const formValue = formValues[key];
    const serverValue = serverValues?.[key as keyof ProjectProps];
    if (!isProjectFieldUnchanged(key, formValue, serverValue)) {
      changed[key] = formValue;
    }
  });

  return changed as Partial<ProjectCreationValues>;
};

/**
 * Media (question stimuli) uploads must only ever accompany a FULL save
 * (TELV1-490 PR review MAJOR finding). Pending media (`fileData`) is not
 * part of the value-diff surface — `normalizeProjectFieldValue`'s
 * `interview_questions` case drops runtime `File`s before comparing — so a partial
 * save (some other field failed validation) has no way to represent "media
 * upload still pending" in what it persists. If a partial save both
 * uploaded the media AND cleared the ready-for-upload flag, a later full
 * save would never re-attempt the upload, silently losing it. Gating both
 * the upload and the flag-clear behind `isFullSave` keeps the flag pending
 * until a save actually completes the whole form, so the next full save
 * always re-attempts it.
 */
export const shouldUploadMediaOnSave = (
  isFullSave: boolean,
  isMediaReadyForUpload: boolean,
): boolean => isFullSave && isMediaReadyForUpload;

/**
 * Whether `interview_questions` needs `trigger()` validation this submit.
 * Value-diff alone (`changedKeys`, derived from `getChangedProjectFields`)
 * is NOT a reliable signal here: a tree whose only content is a
 * newly-created, user-named-but-childless group normalizes to the exact
 * same "empty" value as the server's baseline
 * (`normalizeProjectFieldValue`'s `isInterviewQuestionsEmpty` case), so the
 * value-diff reports no change at all — even though the user did touch the
 * section and left it in a state the schema explicitly rejects ("group
 * cannot be empty").
 *
 * Skipping validation in that case used to route the submit straight past
 * `onError` into `onSubmit`'s own empty-payload branch, which has no way to
 * distinguish "nothing to save" from "invalid, needs a validation toast"
 * and fired the generic TELV1-400 sentinel instead (confirmed via
 * TELLET-DASHBOARD-4MJ: culprit `onSubmit`, `dirtyFieldKeys:
 * ["interview_questions"]`, `interviewQuestionsStructureChanged: true`).
 *
 * `interviewQuestionsStructureChanged` / `reorderChanged` are the dedicated
 * signals group-creation and drag-reorder set directly and are NOT
 * normalized away, so OR-ing them into the validation gate closes the gap:
 * any real edit to `interview_questions` gets validated, regardless of
 * whether its net value happens to diff to "unchanged".
 */
export const needsInterviewQuestionsValidation = (
  changedKeys: ReadonlySet<keyof ProjectCreationValues>,
  interviewQuestionsStructureChanged: boolean,
  reorderChanged: boolean,
): boolean =>
  changedKeys.has('interview_questions') || interviewQuestionsStructureChanged || reorderChanged;

/**
 * Splits `changedFields` (as computed by `getChangedProjectFields`) into
 * the subset that is still valid per the live field state, and the keys
 * that are not. Used by `ProjectCreate`'s `onError` to persist whatever
 * changed fields ARE valid instead of discarding a save entirely when only
 * SOME sections fail validation (TELV1-490 Point 2), while making the
 * "everything that changed is invalid" case an explicit, independently
 * testable value — an empty `validSubset` must never reach
 * `persistProjectPayload` (TELLET-DASHBOARD-4MJ regression guard).
 */
export const partitionChangedFields = (
  changedFields: Partial<ProjectCreationValues>,
  isInvalid: (key: keyof ProjectCreationValues) => boolean,
): {
  validSubset: Partial<ProjectCreationValues>;
  invalidKeys: (keyof ProjectCreationValues)[];
} => {
  const entries = Object.entries(changedFields) as [keyof ProjectCreationValues, unknown][];

  const validSubset = Object.fromEntries(
    entries.filter(([key]) => !isInvalid(key)),
  ) as Partial<ProjectCreationValues>;
  const invalidKeys = entries.filter(([key]) => isInvalid(key)).map(([key]) => key);

  return { validSubset, invalidKeys };
};
